import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../components/AxiosInstance";

// --- API Endpoints & Base URL ---
const API_BASE_URL = "https://api.hvacsync.in";
const CHILLER_BASE_URL = `${API_BASE_URL}/api/machines/`;
// Compressor URL template: CHILLER_BASE_URL + `${chillerId}/compressors/`

// --- Initial Form Data States ---
const initialChillerFormData = {
    company: "",
    mqtt_topic: "",
    name: "",
    description: "",
    image: null,
    manual: false,
    is_default: false,
    is_deleted: false,
    machine_type: "automatic", // For UI select value
};

const initialCompressorFormData = {
    parentChillerId: "", // Required for API URL
    name: "",
    description: "",
    index: 0,
    image: null,
};

const Machine = () => {
    // --- State Management ---
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [isTypeSelectionModalOpen, setIsTypeSelectionModalOpen] = useState(false);
    const [selectedMachineType, setSelectedMachineType] = useState(null); // 'chiller' or 'compressor'

    // Edited item structure: { type: 'chiller'|'compressor', data: {...} }
    const [editedMachine, setEditedMachine] = useState(null);

    const [companyData, setCompanyData] = useState([]);
    // machineData stores combined list: Chillers (Type: 'chiller') and Compressors (Type: 'compressor')
    const [machineData, setMachineData] = useState([]);
    const [chillerData, setChillerData] = useState([]); // Needed for Compressor parent dropdown

    const [formData, setFormData] = useState({ ...initialChillerFormData, ...initialCompressorFormData });
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const authToken = localStorage.getItem("access");

    // --- Utility Functions ---

    // Toggle the main form/modal and set up initial state for Add/Edit
    function toggleForm(machine = null) {
        if (machine) {
            setEditedMachine(machine);
            setSelectedMachineType(machine.type); // Set type for edit
            // Load appropriate data into formData for editing
            setFormData(machine.type === 'chiller' 
                ? { ...initialChillerFormData, ...machine.data, machine_type: machine.data.manual ? "manual" : "automatic" }
                : { ...initialCompressorFormData, ...machine.data, parentChillerId: machine.data.parentChillerId }
            );
            setIsFormVisible(true);
        } else {
            // Close form, reset everything
            setEditedMachine(null);
            setIsFormVisible(false);
            setFormData({ ...initialChillerFormData, ...initialCompressorFormData });
            setSelectedMachineType(null);
        }
    }
    
    // Opens the initial modal to choose machine type for ADD
    const openTypeSelectionModal = () => {
        setEditedMachine(null); // Ensure not in edit mode
        setFormData({ ...initialChillerFormData, ...initialCompressorFormData });
        setIsTypeSelectionModalOpen(true);
    }

    // Handles selecting the machine type and opens the main form
    const handleMachineTypeSelection = (type) => {
        setSelectedMachineType(type);
        setIsTypeSelectionModalOpen(false);
        setIsFormVisible(true);
    };


    // --- Data Fetching ---

    useEffect(() => {
        fetchCompany();
        fetchCombinedMachines();
    }, []);

    // Fetches company list (for Chiller/Compressor Company dropdown)
    const fetchCompany = async () => {
        try {
            const response = await axiosInstance.get(`${API_BASE_URL}/companies/`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            setCompanyData(response.data?.results || []);
        } catch (error) {
            console.error("Error fetching company data:", error.response || error);
        }
    };
    
    // Fetches all Chillers
    const fetchChillers = async () => {
        const response = await axiosInstance.get(CHILLER_BASE_URL, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        return response.data?.data || [];
    }

    // Fetches Compressors for a specific Chiller ID
    const fetchCompressors = async (chillerId) => {
        const url = `${CHILLER_BASE_URL}${chillerId}/compressors/`;
        const response = await axiosInstance.get(url, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        // The API returns an array, but Compressors need their parent Chiller ID
        return (response.data?.data || []).map(comp => ({
            ...comp, 
            parentChillerId: chillerId,
            parentChillerName: chillerData.find(c => c.id === chillerId)?.name || `Chiller ${chillerId}`
        }));
    }

    // Main function to fetch and combine all machine data
    const fetchCombinedMachines = async () => {
        setIsLoading(true);
        try {
            // 1. Fetch all Chillers
            const chillers = await fetchChillers();
            setChillerData(chillers); // Store chillers for the compressor dropdown

            let combinedData = [];
            
            // 2. Map Chillers for the main table
            const chillerList = chillers.map(c => ({ 
                id: c.id, 
                type: 'chiller', 
                name: c.name,
                company: c.company,
                topic: c.mqtt_topic,
                description: c.description,
                image: c.image,
                manual: c.manual,
                data: c // Keep original data for editing
            }));
            
            combinedData.push(...chillerList);

            // 3. Fetch Compressors for each Chiller
            for (const chiller of chillers) {
                const compressors = await fetchCompressors(chiller.id);
                const compressorList = compressors.map(c => ({
                    id: c.id,
                    type: 'compressor',
                    name: c.name,
                    // Use parent chiller's company for display/filtering
                    company: chiller.company, 
                    topic: `N/A (Parent: ${chiller.name})`,
                    description: c.description,
                    image: c.image,
                    manual: c.manual,
                    data: c // Keep original data for editing (including parentChillerId)
                }));
                combinedData.push(...compressorList);
            }
            
            setMachineData(combinedData);

        } catch (error) {
            console.error("Error fetching combined machine data:", error.response || error);
            toast.error("Failed to load machine list.");
            setMachineData([]);
        } finally {
            setIsLoading(false);
        }
    };


    // --- Form Handlers ---

    const handleInputChange = (e) => {
        const { id, value, type, files } = e.target;
        
        if (type === "file") {
            setFormData({ ...formData, image: files[0] });
        } else if (id === "machine_type") {
            // Chiller type toggle
            setFormData({
                ...formData,
                manual: value === "manual",
                machine_type: value,
            });
        } else if (id === "index") {
             // Handle numeric input for index
             setFormData({ ...formData, [id]: parseInt(value) || 0 });
        } else {
            setFormData({ ...formData, [id]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedMachineType) {
            toast.error("Machine type not selected.");
            return;
        }

        try {
            const isEdit = editedMachine !== null;
            let url = "";
            let method = isEdit ? "PATCH" : "POST";

            const formDataObject = new FormData();
            
            if (selectedMachineType === 'chiller') {
                // CHILLER API LOGIC
                url = isEdit ? `${CHILLER_BASE_URL}${editedMachine.data.id}/` : CHILLER_BASE_URL;
                
                // Fields required for Chiller
                formDataObject.append("company", formData.company);
                formDataObject.append("mqtt_topic", formData.mqtt_topic);
                formDataObject.append("name", formData.name);
                formDataObject.append("description", formData.description);
                formDataObject.append("manual", String(formData.manual)); // API expects boolean, but FormData sends string
                formDataObject.append("is_default", String(formData.is_default));
                formDataObject.append("is_deleted", String(formData.is_deleted));
                formDataObject.append("image", "machine_images/chillerbluestar.png");

            } else if (selectedMachineType === 'compressor') {
                // COMPRESSOR API LOGIC
                const chillerId = isEdit ? editedMachine.data.parentChillerId : formData.parentChillerId;
                if (!chillerId) {
                    toast.error("Please select a parent Chiller for the Compressor.");
                    return;
                }
                
                url = isEdit 
                    ? `${CHILLER_BASE_URL}${chillerId}/compressors/${editedMachine.data.id}/` 
                    : `${CHILLER_BASE_URL}${chillerId}/compressors/`;
                
                // Fields required for Compressor
                formDataObject.append("name", formData.name);
                formDataObject.append("description", formData.description);
                formDataObject.append("index", formData.index);
                formDataObject.append("image", "machine_images/chillerbluestar.png");
            }
            
           
            
            const response = await axiosInstance({
                method: method,
                url: url,
                data: formDataObject,
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'multipart/form-data',
                    // Note: Content-Type is typically omitted for FormData
                },
            });

            toast.success(response.data?.message || `${selectedMachineType} saved successfully`);
            toggleForm(null); // Close the form and reset state
            fetchCombinedMachines();

        } catch (error) {
            console.error("Error submitting form:", error.response?.data || error.message);
            // Display specific API validation errors if available
            const errorMsg = error.response?.data?.name?.[0] || 
                             error.response?.data?.mqtt_topic?.[0] ||
                             error.response?.data?.detail || 
                             "Unexpected error occurred during submission";
            toast.error(errorMsg);
        }
    };

    // --- Delete Handler ---

    const handleDeleteMachine = async (machine) => {
        if (!window.confirm(`Are you sure you want to delete this ${machine.type}: ${machine.name}?`)) {
            return;
        }
        
        try {
            let url = "";
            if (machine.type === 'chiller') {
                url = `${CHILLER_BASE_URL}${machine.id}/`;
            } else {
                const chillerId = machine.data.parentChillerId;
                if (!chillerId) throw new Error("Parent Chiller ID missing for compressor delete.");
                url = `${CHILLER_BASE_URL}${chillerId}/compressors/${machine.id}/`;
            }

            const response = await axiosInstance.delete(url, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (response.status === 204 || response.status === 200) {
                toast.success(`${machine.type} Deleted Successfully`);
                fetchCombinedMachines();
            } else {
                console.error("Error deleting data:", response.statusText);
                toast.error(`Failed to delete ${machine.type}.`);
            }
        } catch (error) {
            console.error("Error deleting machine:", error.response || error);
            toast.error(`An error occurred while deleting the ${machine.type}.`);
        }
    };

    // --- Search and Pagination ---

    const handleSearchTermChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const filteredMachine = (machineData || []).filter((machine) => {
        const nameMatch = machine.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const topicMatch = machine.topic?.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Safely extract company name for filtering
        const companyName = 
            typeof machine.company === 'object' && machine.company !== null 
            ? machine.company.name 
            : String(machine.company || '').toLowerCase(); // Fallback for primitive ID/String

        const companyMatch = companyName.toLowerCase().includes(searchTerm.toLowerCase());

        return nameMatch || topicMatch || companyMatch;
    });

    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredMachine.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredMachine.slice(indexOfFirstItem, indexOfLastItem);

    const handlePrevious = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // --- Rendered Component ---

    // Renders the specific form fields based on selectedMachineType
    const renderFormFields = () => {
        if (selectedMachineType === 'chiller') {
            return (
                <>
                    <Row>
                        {/* Company Name */}
                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="company" className="form-label">Company Name</Label>
                                <Input type="select" id="company" value={formData.company} onChange={handleInputChange} required>
                                    <option value="">Select Company</option>
                                    {companyData.map((company) => (
                                        <option key={company.id} value={company.id}>{company.name}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>

                        {/* Machine Name */}
                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="name" className="form-label">Chiller Name</Label>
                                <Input type="text" id="name" placeholder="Chiller Name" value={formData.name} onChange={handleInputChange} required />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        {/* Machine Type (Manual/Automatic) */}
                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="machine_type" className="form-label">Machine Type</Label>
                                <Input type="select" id="machine_type" value={formData.machine_type} onChange={handleInputChange} required>
                                    <option value="">Select Type</option>
                                    <option value="automatic">Automatic (Telemetry)</option>
                                    <option value="manual">Manual (Data Entry)</option>
                                </Input>
                            </FormGroup>
                        </Col>

                        {/* Topic */}
                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="mqtt_topic" className="form-label">MQTT Topic</Label>
                                <Input type="text" id="mqtt_topic" placeholder="e.g. v1/device/me/topic123" value={formData.mqtt_topic} onChange={handleInputChange} required={formData.machine_type === 'automatic'} disabled={formData.machine_type === 'manual'} />
                                {formData.machine_type === 'manual' && <small className="text-muted">MQTT Topic is not required for Manual machines.</small>}
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            );
        } else if (selectedMachineType === 'compressor') {
            return (
                <>
                    <Row>
                         {/* Parent Chiller Selector (ONLY FOR ADD/EDIT COMPRESSOR) */}
                         <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="parentChillerId" className="form-label">Parent Chiller</Label>
                                <Input 
                                    type="select" 
                                    id="parentChillerId" 
                                    value={formData.parentChillerId} 
                                    onChange={handleInputChange} 
                                    required
                                    // Disable selection in EDIT mode for consistency
                                    disabled={editedMachine !== null} 
                                >
                                    <option value="">Select Parent Chiller</option>
                                    {chillerData.map((chiller) => (
                                        <option key={chiller.id} value={chiller.id}>{chiller.name}</option>
                                    ))}
                                </Input>
                                {editedMachine && <small className="text-muted">Parent Chiller cannot be changed during edit.</small>}
                            </FormGroup>
                        </Col>
                        {/* Compressor Name */}
                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="name" className="form-label">Compressor Name</Label>
                                <Input type="text" id="name" placeholder="Compressor Name" value={formData.name} onChange={handleInputChange} required />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        {/* Index */}
                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label htmlFor="index" className="form-label">Index (Order)</Label>
                                <Input type="number" id="index" placeholder="0" value={formData.index} onChange={handleInputChange} required />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            );
        }
        return null;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="HVACSYNC" breadcrumbItem="Machines" />

                    {/* --- Type Selection Modal (FOR ADD) --- */}
                    <Modal isOpen={isTypeSelectionModalOpen} toggle={() => setIsTypeSelectionModalOpen(false)} centered>
                        <ModalHeader toggle={() => setIsTypeSelectionModalOpen(false)}>
                            Select Machine Type
                        </ModalHeader>
                        <ModalBody className="text-center py-4">
                            <p className="mb-4">Which type of machine would you like to add?</p>
                            <Button color="primary" onClick={() => handleMachineTypeSelection('chiller')} className="w-100 mb-3">
                                <i className="ri-building-line align-bottom me-2"></i> Chiller (Main Unit)
                            </Button>
                            <Button color="info" onClick={() => handleMachineTypeSelection('compressor')} className="w-100">
                                <i className="ri-temp-hot-line align-bottom me-2"></i> Compressor (Sub-unit)
                            </Button>
                        </ModalBody>
                    </Modal>

                    <Row>
                        {/* --- Dynamic Form Column (Chiller/Compressor) --- */}
                        {isFormVisible && (
                            <Col lg={12}>
                                <Card>
                                    <CardHeader className="bg-light p-3">
                                        <h4 className="card-title mb-0">
                                            {editedMachine 
                                                ? `Edit ${editedMachine.type.charAt(0).toUpperCase() + editedMachine.type.slice(1)}` 
                                                : `Add New ${selectedMachineType.charAt(0).toUpperCase() + selectedMachineType.slice(1)}`}
                                            <Button close onClick={() => toggleForm(null)} className="float-end" />
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Form className="tablelist-form" onSubmit={handleSubmit}>
                                            {/* Hidden ID field for edit */}
                                            {editedMachine && (
                                                <input type="hidden" id="id" value={editedMachine.data.id} readOnly />
                                            )}

                                            {/* RENDER DYNAMIC FIELDS HERE */}
                                            {renderFormFields()}

                                            {/* Description (Common to both) */}
                                            <FormGroup className="mb-3">
                                                <Label htmlFor="description" className="form-label">Description</Label>
                                                <Input type="textarea" id="description" className="form-control" placeholder="Description" value={formData.description} onChange={handleInputChange} required />
                                            </FormGroup>

                                          

                                            {/* Form Actions */}
                                            <div className="hstack gap-2 justify-content-end mt-4">
                                                <Button type="button" color="light" onClick={() => toggleForm(null)}>
                                                    Cancel
                                                </Button>
                                                <Button type="submit" color="success" id="add-btn">
                                                    {editedMachine ? `Update ${selectedMachineType}` : `Add ${selectedMachineType}`}
                                                </Button>
                                            </div>
                                        </Form>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}

                        {/* --- Main Content Column --- */}
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">Machine List</h4>
                                </CardHeader>

                                <CardBody>
                                    <div id="customerList">
                                        <Row className="g-4 mb-3">
                                            <Col className="col-sm-auto">
                                                <div className="d-flex gap-1">
                                                    {/* Button to open the type selection modal */}
                                                    <Button
                                                        color="success"
                                                        className="add-btn"
                                                        onClick={openTypeSelectionModal}
                                                        id="create-btn"
                                                    >
                                                        <i className="ri-add-line align-bottom me-1"></i>{" "}
                                                        Add New Machine
                                                    </Button>
                                                </div>
                                            </Col>
                                            <Col className="col-sm">
                                                <div className="d-flex justify-content-sm-end">
                                                    <div className="search-box ms-2">
                                                        <input
                                                            type="text"
                                                            className="form-control search"
                                                            placeholder="Search by Name, Company, or Topic..."
                                                            value={searchTerm}
                                                            onChange={handleSearchTermChange}
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table
                                                className="table align-middle table-nowrap"
                                                id="customerTable"
                                            >
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Type</th>
                                                        <th>Logo</th>
                                                        <th className="sort">Company Name</th>
                                                        <th className="sort">Machine Name</th>
                                                        <th className="sort">Operation Type</th>
                                                        <th className="sort">Topic/Parent</th>
                                                        <th className="sort">Description</th>
                                                        <th className="sort">Action</th>
                                                    </tr>
                                                </thead>
                                                {isLoading ? (
                                                    <tbody>
                                                        <tr>
                                                            <td colSpan="8" className="text-center">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                ) : (
                                                    <tbody className="list form-check-all">
                                                        {currentItems.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="8" className="text-center">
                                                                    No machines found
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            currentItems.map((machine, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <span className={`badge ${machine.type === 'chiller' ? 'bg-primary' : 'bg-info'}`}>
                                                                            {machine.type.charAt(0).toUpperCase() + machine.type.slice(1)}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {machine.image && (
                                                                            <div className="logo">
                                                                                <img
                                                                                    src={`${API_BASE_URL}/media/${machine.image}`}
                                                                                    alt="machine"
                                                                                    height="48"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {/* Display company name safely */}
                                                                        {typeof machine.company === 'object' && machine.company !== null ? machine.company.name : machine.company || '-'}
                                                                    </td>
                                                                    <td>{machine.name}</td>
                                                                    <td>
                                                                        {machine.manual ? "Manual" : "Automatic"}
                                                                    </td>
                                                                    <td>
                                                                        {machine.type === 'chiller' ? machine.topic : machine.topic} 
                                                                    </td>
                                                                    <td>{machine.description}</td>

                                                                    <td>
                                                                        <div className="d-flex gap-2">
                                                                            <div className="edit">
                                                                                <button
                                                                                    className="btn btn-warning"
                                                                                    onClick={() => toggleForm(machine)}
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            </div>
                                                                            <div className="remove">
                                                                                <button
                                                                                    className="btn btn-danger remove-item-btn"
                                                                                    onClick={() => handleDeleteMachine(machine)}
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                )}
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="d-flex justify-content-end">
                                            <div className="pagination-wrap hstack gap-2">
                                                <Link
                                                    className={`page-item pagination-prev ${currentPage === 1 ? "disabled" : ""}`}
                                                    to="#"
                                                    onClick={handlePrevious}
                                                >
                                                    Previous
                                                </Link>
                                                <ul className="pagination listjs-pagination mb-0">
                                                    <li>
                                                        Page {currentPage} of {totalPages}
                                                    </li>
                                                </ul>
                                                <Link
                                                    className={`page-item pagination-next ${currentPage === totalPages ? "disabled" : ""}`}
                                                    to="#"
                                                    onClick={handleNext}
                                                >
                                                    Next
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Machine;